/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import Loading from '../../components/common/Loading';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 94vh;
  overflow-y: auto;
  width: 100%;
  margin: 16px 16px 16px 0;
  padding: 28px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 24px 0;
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-size: 15px;
  font-weight: 600;
  color: ${({ $active }) => $active ? '#007aff' : '#86868b'};
  border-bottom: 2px solid ${({ $active }) => $active ? '#007aff' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: #1d1d1f; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 16px 0;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1d1d1f;
  font-family: 'SF Mono', monospace;
`;

const SummaryLabel = styled.div`
  font-size: 13px;
  color: #86868b;
  margin-top: 4px;
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td { padding: 10px 14px; font-size: 14px; border-bottom: 1px solid #f3f4f6; text-align: left; }
  thead tr { background: #2d3748; }
  th { font-weight: 600; color: #fff; font-size: 13px; }
  td { color: #1d1d1f; }
  tbody tr:hover { background: rgba(0,122,255,0.04); }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const Btn = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $variant }) =>
    $variant === 'primary' ? '#007aff' :
    $variant === 'excel' ? '#1d6f42' : '#f3f4f6'};
  color: ${({ $variant }) => ($variant === 'primary' || $variant === 'excel') ? '#fff' : '#374151'};
  &:hover { opacity: 0.85; }
`;

const Select = styled.select`
  padding: 7px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  background: #fff;
`;

const formatMoney = (n) => n ? n.toLocaleString('ko-KR') : '0';

const PERIODS = [
  { key: 'thisMonth', label: '이번 달' },
  { key: 'lastMonth', label: '지난 달' },
  { key: 'thisYear', label: '올해' },
  { key: 'all', label: '전체' },
];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${dd}`;
};

const getPeriodDates = (key) => {
  const now = new Date();
  if (key === 'thisMonth') {
    return { from: `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.01`, to: toDateStr(now) };
  }
  if (key === 'lastMonth') {
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toDateStr(lm), to: toDateStr(lmEnd) };
  }
  if (key === 'thisYear') {
    return { from: `${now.getFullYear()}.01.01`, to: toDateStr(now) };
  }
  return { from: '', to: '' };
};

const DateInput = styled.input`
  padding: 6px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  font-family: inherit;
  &:focus { border-color: #007aff; }
`;

const StatsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('monthly');
  const [monthly, setMonthly] = useState([]);
  const [customers, setCustomers] = useState({ customers: [], totalRevenue: 0, totalOrders: 0 });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState({ mainCategories: [], subCategories: [] });
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('thisMonth');
  const initDates = getPeriodDates('thisMonth');
  const [customFrom, setCustomFrom] = useState(initDates.from);
  const [customTo, setCustomTo] = useState(initDates.to);

  const getDateParams = (p, cFrom, cTo) => {
    const per = p || period;
    if (per === 'custom') {
      const f = cFrom || customFrom;
      const t = cTo || customTo;
      return f && t ? `?from=${f}&to=${t}` : '';
    }
    const { from, to } = getPeriodDates(per);
    return from ? `?from=${from}&to=${to}` : '';
  };

  const [monthlyPeriod, setMonthlyPeriod] = useState('12months');

  const getMonthlyDates = (p) => {
    const per = p || monthlyPeriod;
    const now = new Date();
    if (per === 'thisMonth') {
      return { from: `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.01`, to: toDateStr(now) };
    }
    if (per === 'lastMonth') {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: toDateStr(lm), to: toDateStr(lmEnd) };
    }
    if (per === 'thisYear') {
      return { from: `${now.getFullYear()}.01.01`, to: toDateStr(now) };
    }
    if (per === '12months') {
      const ago = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      return { from: toDateStr(ago), to: toDateStr(now) };
    }
    if (per === 'all') return { from: '', to: '' };
    // custom
    return { from: customFrom, to: customTo };
  };

  const fetchMonthly = async (p, cFrom, cTo) => {
    setLoading(true);
    try {
      const per = p || monthlyPeriod;
      let f, t;
      if (per === 'custom') {
        f = cFrom || customFrom;
        t = cTo || customTo;
      } else {
        const d = getMonthlyDates(per);
        f = d.from; t = d.to;
      }
      const params = f ? `?from=${f}&to=${t}&months=60` : '?months=60';
      const url = `/admin/stats/monthly${params}`;
      const { data } = await axios.get(url);
      setMonthly(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCustomers = async (p, cFrom, cTo) => {
    setLoading(true);
    try {
      const params = getDateParams(p, cFrom, cTo);
      const { data } = await axios.get(`/admin/stats/customers${params}`);
      setCustomers(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchProducts = async (p, cFrom, cTo) => {
    setLoading(true);
    try {
      const params = getDateParams(p, cFrom, cTo);
      const { data } = await axios.get(`/admin/stats/products${params}`);
      setProducts(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCategories = async (p, cFrom, cTo) => {
    setLoading(true);
    try {
      const params = getDateParams(p, cFrom, cTo);
      const { data } = await axios.get(`/admin/stats/categories${params}`);
      setCategories(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCurrent = (p, cFrom, cTo) => {
    if (tab === 'monthly') fetchMonthly(monthlyPeriod);
    else if (tab === 'customer') fetchCustomers(p, cFrom, cTo);
    else if (tab === 'product') fetchProducts(p, cFrom, cTo);
    else if (tab === 'category') fetchCategories(p, cFrom, cTo);
  };

  useEffect(() => {
    fetchCurrent();
  }, [tab]);

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setPeriod(val);
    if (val !== 'custom') {
      const { from, to } = getPeriodDates(val);
      setCustomFrom(from);
      setCustomTo(to);
      fetchCurrent(val);
    }
  };

  const handleCustomDate = (from, to) => {
    setCustomFrom(from);
    setCustomTo(to);
    setPeriod('custom');
    if (from && to) fetchCurrent('custom', from, to);
  };

  const downloadExcel = (data, headers, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = headers.map((h) => ({ wch: h.w || 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename);
  };

  const monthlyTotal = monthly.reduce((s, m) => ({ revenue: s.revenue + m.revenue, orders: s.orders + m.orders, items: s.items + m.items }), { revenue: 0, orders: 0, items: 0 });

  return (
    <Container>
      <Title>판매 통계</Title>
      <TabRow>
        <Tab $active={tab === 'monthly'} onClick={() => setTab('monthly')}>월별 통계</Tab>
        <Tab $active={tab === 'customer'} onClick={() => setTab('customer')}>고객별 통계</Tab>
        <Tab $active={tab === 'product'} onClick={() => setTab('product')}>상품별 통계</Tab>
        <Tab $active={tab === 'category'} onClick={() => setTab('category')}>카테고리별 통계</Tab>
      </TabRow>

      {loading && <Loading />}

      {/* 월별 */}
      {tab === 'monthly' && !loading && (
        <>
          <Toolbar>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select value={monthlyPeriod} onChange={(e) => {
                const val = e.target.value;
                setMonthlyPeriod(val);
                if (val !== 'custom') {
                  const d = getMonthlyDates(val);
                  setCustomFrom(d.from); setCustomTo(d.to);
                  fetchMonthly(val);
                }
              }}>
                <option value="12months">최근 12개월</option>
                <option value="thisMonth">이번 달</option>
                <option value="lastMonth">지난 달</option>
                <option value="thisYear">올해</option>
                <option value="all">전체</option>
                <option value="custom">기간 지정</option>
              </Select>
              <DateInput type="date" value={(() => { const d = monthlyPeriod === 'custom' ? customFrom : getMonthlyDates().from; return d ? d.replace(/\./g, '-') : ''; })()}
                onChange={(e) => { const v = e.target.value.replace(/-/g, '.'); setCustomFrom(v); setMonthlyPeriod('custom'); fetchMonthly('custom', v, customTo || toDateStr(new Date())); }} />
              <span style={{ color: '#86868b' }}>~</span>
              <DateInput type="date" value={(() => { const d = monthlyPeriod === 'custom' ? customTo : getMonthlyDates().to; return d ? d.replace(/\./g, '-') : ''; })()}
                onChange={(e) => { const v = e.target.value.replace(/-/g, '.'); setCustomTo(v); setMonthlyPeriod('custom'); fetchMonthly('custom', customFrom || '2020.01.01', v); }} />
            </div>
          </Toolbar>
          <SummaryRow>
            <SummaryCard>
              <SummaryValue>{formatMoney(monthlyTotal.revenue)}</SummaryValue>
              <SummaryLabel>총 매출 (원)</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{formatMoney(monthlyTotal.orders)}</SummaryValue>
              <SummaryLabel>총 주문 건수</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{formatMoney(monthlyTotal.items)}</SummaryValue>
              <SummaryLabel>총 판매 수량</SummaryLabel>
            </SummaryCard>
          </SummaryRow>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <CardTitle style={{ margin: 0 }}>월별 매출 추이</CardTitle>
              <Btn $variant="excel" onClick={() => downloadExcel(
                monthly.map((m) => ({ '월': m.month, '매출': m.revenue, '주문수': m.orders, '판매수량': m.items })),
                [{ w: 10 }, { w: 15 }, { w: 10 }, { w: 10 }],
                '월별매출통계.xlsx'
              )}>엑셀</Btn>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthly} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#86868b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#86868b' }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(0)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(value) => formatMoney(value) + '원'} labelStyle={{ fontWeight: 700 }} />
                <Legend />
                <Bar dataKey="revenue" name="매출" fill="#007aff" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <CardTitle>월별 상세</CardTitle>
            <StatsTable>
              <thead>
                <tr><th>월</th><th style={{ textAlign: 'right' }}>매출</th><th style={{ textAlign: 'right' }}>주문수</th><th style={{ textAlign: 'right' }}>판매수량</th><th></th></tr>
              </thead>
              <tbody>
                {[...monthly].reverse().map((m) => {
                  // "2026.04" → from: "2026.04.01", to: "2026.04.30"
                  const parts = m.month.split('.');
                  const y = parseInt(parts[0], 10);
                  const mo = parseInt(parts[1], 10);
                  const lastDay = new Date(y, mo, 0).getDate();
                  const from = `${m.month}.01`;
                  const to = `${m.month}.${String(lastDay).padStart(2, '0')}`;
                  return (
                  <tr key={m.month}>
                    <td style={{ fontWeight: 600 }}>{m.month}</td>
                    <td style={{ textAlign: 'right', fontFamily: "'SF Mono', monospace" }}>{formatMoney(m.revenue)}</td>
                    <td style={{ textAlign: 'right' }}>{m.orders}</td>
                    <td style={{ textAlign: 'right' }}>{m.items}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Btn style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => navigate(`/order/list?from=${from}&to=${to}`)}>
                        주문보기
                      </Btn>
                    </td>
                  </tr>);
                })}
              </tbody>
            </StatsTable>
          </Card>
        </>
      )}

      {/* 고객별 */}
      {tab === 'customer' && !loading && (
        <>
          <Toolbar>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select value={period} onChange={handlePeriodChange}>
                {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                <option value="custom">기간 지정</option>
              </Select>
              <DateInput type="date" value={customFrom.replace(/\./g, '-')}
                onChange={(e) => handleCustomDate(e.target.value.replace(/-/g, '.'), customTo || toDateStr(new Date()))} />
              <span style={{ color: '#86868b' }}>~</span>
              <DateInput type="date" value={customTo.replace(/\./g, '-')}
                onChange={(e) => handleCustomDate(customFrom || '2020.01.01', e.target.value.replace(/-/g, '.'))} />
              <span style={{ fontSize: 13, color: '#86868b' }}>
                총 매출 {formatMoney(customers.totalRevenue)}원 / {customers.totalOrders}건
              </span>
            </div>
            <Btn $variant="excel" onClick={() => downloadExcel(
              customers.customers.map((c) => ({ '회사': c.company, '매출': c.revenue, '주문수': c.orders, '수량': c.items })),
              [{ w: 25 }, { w: 15 }, { w: 10 }, { w: 10 }],
              '고객별매출통계.xlsx'
            )}>엑셀</Btn>
          </Toolbar>

          <Card>
            <CardTitle>고객별 매출 TOP 10</CardTitle>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={customers.customers.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 100, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#86868b' }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(0)}M` : `${(v / 1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="company" tick={{ fontSize: 12, fill: '#1d1d1f' }} width={90} />
                <Tooltip formatter={(value) => formatMoney(value) + '원'} />
                <Bar dataKey="revenue" name="매출" fill="#34d399" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <CardTitle>고객별 상세 ({customers.customers.length}개사)</CardTitle>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <StatsTable>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>회사</th>
                    <th style={{ textAlign: 'right' }}>매출</th>
                    <th style={{ textAlign: 'right' }}>주문수</th>
                    <th style={{ textAlign: 'right' }}>수량</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {customers.customers.map((c, i) => (
                    <tr key={c.company}>
                      <td style={{ color: '#86868b' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{c.company}</td>
                      <td style={{ textAlign: 'right', fontFamily: "'SF Mono', monospace" }}>{formatMoney(c.revenue)}</td>
                      <td style={{ textAlign: 'right' }}>{c.orders}</td>
                      <td style={{ textAlign: 'right' }}>{c.items}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Btn style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => {
                          const { from, to } = period === 'custom' ? { from: customFrom, to: customTo } : getPeriodDates(period);
                          navigate(`/order/list?q=${encodeURIComponent(c.company)}${from ? `&from=${from}&to=${to}` : ''}`);
                        }}>주문보기</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </StatsTable>
            </div>
          </Card>
        </>
      )}

      {/* 상품별 */}
      {tab === 'product' && !loading && (
        <>
          <Toolbar>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select value={period} onChange={handlePeriodChange}>
                {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                <option value="custom">기간 지정</option>
              </Select>
              <DateInput type="date" value={customFrom.replace(/\./g, '-')}
                onChange={(e) => handleCustomDate(e.target.value.replace(/-/g, '.'), customTo || toDateStr(new Date()))} />
              <span style={{ color: '#86868b' }}>~</span>
              <DateInput type="date" value={customTo.replace(/\./g, '-')}
                onChange={(e) => handleCustomDate(customFrom || '2020.01.01', e.target.value.replace(/-/g, '.'))} />
              <span style={{ fontSize: 13, color: '#86868b' }}>
                총 {products.length}개 상품
              </span>
            </div>
            <Btn $variant="excel" onClick={() => downloadExcel(
              products.map((p) => ({ '상품': p.product, '매출': p.revenue, '판매수량': p.quantity, '주문수': p.orders })),
              [{ w: 35 }, { w: 15 }, { w: 10 }, { w: 10 }],
              '상품별매출통계.xlsx'
            )}>엑셀</Btn>
          </Toolbar>

          <Card>
            <CardTitle>상품별 매출 TOP 10</CardTitle>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={products.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 140, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#86868b' }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(0)}M` : `${(v / 1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="product" tick={{ fontSize: 11, fill: '#1d1d1f' }} width={130} />
                <Tooltip formatter={(value) => formatMoney(value) + '원'} />
                <Bar dataKey="revenue" name="매출" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <CardTitle>상품별 상세</CardTitle>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <StatsTable>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>상품명</th>
                    <th style={{ textAlign: 'right' }}>매출</th>
                    <th style={{ textAlign: 'right' }}>판매수량</th>
                    <th style={{ textAlign: 'right' }}>주문수</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={p.product}>
                      <td style={{ color: '#86868b' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{p.product}</td>
                      <td style={{ textAlign: 'right', fontFamily: "'SF Mono', monospace" }}>{formatMoney(p.revenue)}</td>
                      <td style={{ textAlign: 'right' }}>{p.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{p.orders}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Btn style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => {
                          const { from, to } = period === 'custom' ? { from: customFrom, to: customTo } : getPeriodDates(period);
                          navigate(`/order/list?q=${encodeURIComponent(p.product)}${from ? `&from=${from}&to=${to}` : ''}`);
                        }}>주문보기</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </StatsTable>
            </div>
          </Card>
        </>
      )}

      {/* 카테고리별 */}
      {tab === 'category' && !loading && (
        <>
          <Toolbar>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select value={period} onChange={handlePeriodChange}>
                {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                <option value="custom">기간 지정</option>
              </Select>
              <DateInput type="date" value={customFrom.replace(/\./g, '-')}
                onChange={(e) => handleCustomDate(e.target.value.replace(/-/g, '.'), customTo || toDateStr(new Date()))} />
              <span style={{ color: '#86868b' }}>~</span>
              <DateInput type="date" value={customTo.replace(/\./g, '-')}
                onChange={(e) => handleCustomDate(customFrom || '2020.01.01', e.target.value.replace(/-/g, '.'))} />
              <span style={{ fontSize: 13, color: '#86868b' }}>
                제조사 {categories.mainCategories.length}개 / 카테고리 {categories.subCategories.length}개
              </span>
            </div>
            <Btn $variant="excel" onClick={() => downloadExcel(
              [
                ...categories.mainCategories.map((c) => ({ '구분': '제조사', '카테고리': c.category, '매출': c.revenue, '수량': c.quantity, '주문수': c.orders })),
                ...categories.subCategories.map((c) => ({ '구분': '세부', '카테고리': c.category, '매출': c.revenue, '수량': c.quantity, '주문수': c.orders })),
              ],
              [{ w: 8 }, { w: 30 }, { w: 15 }, { w: 10 }, { w: 10 }],
              '카테고리별매출통계.xlsx'
            )}>엑셀</Btn>
          </Toolbar>

          <Card>
            <CardTitle>제조사별 매출</CardTitle>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={categories.mainCategories.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 100, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#86868b' }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(0)}M` : `${(v / 1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 12, fill: '#1d1d1f' }} width={90} />
                <Tooltip formatter={(value) => formatMoney(value) + '원'} />
                <Bar dataKey="revenue" name="매출" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <CardTitle>제조사별 상세</CardTitle>
            <StatsTable>
              <thead>
                <tr>
                  <th>#</th>
                  <th>제조사</th>
                  <th style={{ textAlign: 'right' }}>매출</th>
                  <th style={{ textAlign: 'right' }}>수량</th>
                  <th style={{ textAlign: 'right' }}>주문수</th>
                </tr>
              </thead>
              <tbody>
                {categories.mainCategories.map((c, i) => (
                  <tr key={c.category}>
                    <td style={{ color: '#86868b' }}>{i + 1}</td>
                    <td style={{ fontWeight: 700 }}>{c.category}</td>
                    <td style={{ textAlign: 'right', fontFamily: "'SF Mono', monospace" }}>{formatMoney(c.revenue)}</td>
                    <td style={{ textAlign: 'right' }}>{c.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{c.orders}</td>
                  </tr>
                ))}
              </tbody>
            </StatsTable>
          </Card>

          <Card>
            <CardTitle>세부 카테고리별 상세</CardTitle>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <StatsTable>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>카테고리</th>
                    <th style={{ textAlign: 'right' }}>매출</th>
                    <th style={{ textAlign: 'right' }}>수량</th>
                    <th style={{ textAlign: 'right' }}>주문수</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.subCategories.map((c, i) => (
                    <tr key={c.category}>
                      <td style={{ color: '#86868b' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{c.category}</td>
                      <td style={{ textAlign: 'right', fontFamily: "'SF Mono', monospace" }}>{formatMoney(c.revenue)}</td>
                      <td style={{ textAlign: 'right' }}>{c.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{c.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </StatsTable>
            </div>
          </Card>
        </>
      )}
    </Container>
  );
};

export default StatsPage;
